'use client';

import {
	Analytics,
	getAnalytics,
	logEvent,
	setUserProperties,
	setUserId,
} from 'firebase/analytics';
import type { FirebaseApp } from 'firebase/app';
import type { IAnalyticsService } from '../types/analytics.types';
import { logger } from '@/core/logger';

class FirebaseAnalyticsService implements IAnalyticsService {
	private initialized: boolean = false;
	private analytics: Analytics | null = null;

	async initialize(app: FirebaseApp): Promise<boolean> {
		// Prevent re-initialization
		if (this.initialized) {
			return true;
		}

		// Check if running in browser
		if (typeof window === 'undefined') {
			logger.info('Analytics skipped: not in browser environment');
			return false;
		}

		try {
			// Check if measurementId is configured
			if (!app.options.measurementId) {
				logger.info('Analytics skipped: measurementId not configured');
				return false;
			}

			// Initialize Analytics with provided app
			this.analytics = getAnalytics(app);

			if (this.analytics) {
				this.initialized = true;

				return true;
			}

			return false;
		} catch (error) {
			logger.error('Failed to initialize Analytics', error);
			return false;
		}
	}

	logEvent(
		eventName: string,
		eventParameters?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
	): void {
		if (!this.analytics || !this.initialized) {
			return;
		}

		try {
			logEvent(this.analytics, eventName, eventParameters);
		} catch (error) {
			logger.error(`Error logging analytics event: ${eventName}`, error);
		}
	}

	logError(
		eventName: string,
		eventParameters?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
	): void {
		this.logEvent(eventName, eventParameters);
	}

	setUserProperties(
		properties: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
	): void {
		if (!this.analytics || !this.initialized) {
			return;
		}

		try {
			setUserProperties(this.analytics, properties);
		} catch (error) {
			logger.error('Error setting user properties', error);
		}
	}

	setUserId(userId: string): void {
		if (!this.analytics || !this.initialized) {
			return;
		}

		try {
			setUserId(this.analytics, userId);
		} catch (error) {
			logger.error('Error setting user ID', error);
		}
	}

	/**
	 * Identify user with optional properties (unified-style method)
	 * For Firebase, this calls setUserId and setUserProperties separately
	 */
	identify(
		userId: string,
		userProperties?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
	): void {
		this.setUserId(userId);
		if (userProperties) {
			this.setUserProperties(userProperties);
		}
	}
}

export const firebaseAnalyticsService = new FirebaseAnalyticsService();
